import React, { useRef, useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Calendar, HelpCircle } from 'lucide-react';

interface DateInputProps {
  label?: string;
  value?: string; // ISO format: YYYY-MM-DD
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  hint?: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  min?: string;
  max?: string;
}

/**
 * Formatează data din ISO (YYYY-MM-DD) în format românesc (DD-MM-YYYY)
 */
export function formatDateDisplay(isoDate?: string): string {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
}

/**
 * Parsează dd-mm-yyyy sau dd/mm/yyyy sau ddmmyyyy → yyyy-mm-dd
 */
function parseUserDate(input: string): string | null {
  const cleaned = input.trim();
  // dd-mm-yyyy sau dd/mm/yyyy sau dd.mm.yyyy
  const match = cleaned.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    const day = d.padStart(2, '0');
    const month = m.padStart(2, '0');
    if (parseInt(month) >= 1 && parseInt(month) <= 12 && parseInt(day) >= 1 && parseInt(day) <= 31) {
      return `${y}-${month}-${day}`;
    }
  }
  // ddmmyyyy (8 cifre fără separator)
  const match2 = cleaned.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (match2) {
    const [, d, m, y] = match2;
    if (parseInt(m) >= 1 && parseInt(m) <= 12 && parseInt(d) >= 1 && parseInt(d) <= 31) {
      return `${y}-${m}-${d}`;
    }
  }
  return null;
}

export function DateInput({
  label,
  value,
  onChange,
  error,
  hint,
  tooltip,
  required,
  disabled,
  className,
  id,
  min,
  max,
}: DateInputProps) {
  const hiddenRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const inputId = id || `date-input-${Math.random().toString(36).substr(2, 9)}`;

  const [textValue, setTextValue] = useState(() => formatDateDisplay(value));
  const [focused, setFocused] = useState(false);

  // Sync text when value prop changes from outside
  useEffect(() => {
    if (!focused) {
      setTextValue(formatDateDisplay(value));
    }
  }, [value, focused]);

  // Emit a synthetic onChange that looks like a native input event
  const emitChange = (isoValue: string) => {
    if (!onChange || !hiddenRef.current) return;
    // Set value on hidden input and dispatch from it
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(hiddenRef.current, isoValue);
    }
    const event = new Event('input', { bubbles: true });
    hiddenRef.current.dispatchEvent(event);
    // Also call onChange directly with a synthetic event shape
    onChange({
      target: { value: isoValue },
      currentTarget: { value: isoValue },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setTextValue(raw);
    // Try to parse as you type
    const iso = parseUserDate(raw);
    if (iso) {
      emitChange(iso);
    } else if (raw === '') {
      emitChange('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const iso = parseUserDate(textValue);
    if (iso) {
      setTextValue(formatDateDisplay(iso));
      emitChange(iso);
    } else if (textValue === '') {
      emitChange('');
    } else {
      // Revert to last valid value
      setTextValue(formatDateDisplay(value));
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  // Picker din calendar icon
  const openPicker = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && hiddenRef.current) {
      hiddenRef.current.showPicker?.();
    }
  };

  // Când se selectează din picker
  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isoVal = e.target.value;
    setTextValue(formatDateDisplay(isoVal));
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1"
        >
          <span>{label}</span>
          {required && <span className="text-danger-500">*</span>}
          {tooltip && (
            <span title={tooltip} className="cursor-help text-gray-400 hover:text-gray-600">
              <HelpCircle className="w-3.5 h-3.5" />
            </span>
          )}
        </label>
      )}
      <div className="relative">
        {/* Input text editabil — userul poate tasta dd-mm-yyyy */}
        <input
          ref={textRef}
          id={inputId}
          type="text"
          value={textValue}
          onChange={handleTextChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          placeholder="ZZ-LL-AAAA"
          className={clsx(
            'w-full rounded-lg border shadow-sm transition-colors text-sm',
            'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'hover:border-primary-400',
            disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed',
            error ? 'border-danger-500' : 'border-gray-300',
            'pl-3 pr-10 py-2',
            className
          )}
        />
        {/* Buton calendar — deschide picker-ul nativ */}
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Calendar className="w-4 h-4" />
        </button>
        {/* Input date ascuns — doar pentru picker */}
        <input
          ref={hiddenRef}
          type="date"
          value={value || ''}
          onChange={handlePickerChange}
          disabled={disabled}
          min={min}
          max={max}
          className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      {error && <p className="mt-1 text-sm text-danger-500">{error}</p>}
      {hint && !error && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
    </div>
  );
}

DateInput.displayName = 'DateInput';
